import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { PageHeader, StatusPill } from "../components/shared/Primitives";
import { ArrowLeft } from "lucide-react";

const SOURCE_DOT = {
  "Daily To-Do": "var(--rw-dot-todo)",
  "Pipeline": "var(--rw-dot-pipeline)",
  "Calling": "var(--rw-dot-calling)",
  "Introductions": "var(--rw-dot-intros)",
  "Call Schedule": "var(--rw-dot-schedule)",
  "Networking": "#8E4EC6",
};

export default function ContactDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [contact, setContact] = useState(null);
  const [activity, setActivity] = useState([]);
  const [openItem, setOpenItem] = useState(null);

  const load = async () => {
    const [c, a] = await Promise.all([
      api.get(`/contacts`).then((r) => r.data.find((x) => x.id === id)),
      api.get(`/contacts/${id}/activity`).then((r) => r.data),
    ]);
    setContact(c);
    setActivity(a);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  if (!contact) return <div className="p-8 text-neutral-500">Loading…</div>;

  return (
    <div>
      <PageHeader
        eyebrow="Contact"
        title={`${contact.first_name} ${contact.last_name}`}
        description={contact.company}
        right={
          <Button variant="outline" onClick={() => nav(-1)}>
            <ArrowLeft size={14} className="mr-2" /> Back
          </Button>
        }
      />
      <div className="p-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-[var(--rw-border)] rounded-[4px] p-5 lg:col-span-1">
          <div className="label-eyebrow mb-3">Details</div>
          <dl className="space-y-2 text-sm">
            <div>
              <dt className="text-xs text-neutral-500">Email</dt>
              <dd className="font-mono-rw text-xs">{contact.email || "—"}</dd>
            </div>
            <div>
              <dt className="text-xs text-neutral-500">Phone</dt>
              <dd className="font-mono-rw text-xs">{contact.phone || "—"}</dd>
            </div>
            <div>
              <dt className="text-xs text-neutral-500">Company</dt>
              <dd>{contact.company || "—"}</dd>
            </div>
            <div>
              <dt className="text-xs text-neutral-500">Notes</dt>
              <dd className="text-neutral-700 whitespace-pre-wrap">{contact.notes || "—"}</dd>
            </div>
            <div>
              <dt className="text-xs text-neutral-500">Tags</dt>
              <dd className="flex flex-wrap gap-1 mt-1">
                {(contact.tags || []).length === 0 && <span className="text-xs text-neutral-400">none</span>}
                {(contact.tags || []).map((t) => (
                  <span key={t} className="text-[10px] bg-neutral-100 border border-neutral-200 px-1.5 py-0.5 rounded">
                    {t}
                  </span>
                ))}
              </dd>
            </div>
          </dl>
        </div>

        <div className="lg:col-span-2 space-y-3">
          <div className="label-eyebrow mb-2">Related CRM Activity</div>
          {activity.length === 0 && (
            <div className="bg-white border border-[var(--rw-border)] rounded-[4px] p-6 text-sm text-neutral-400">
              No related activity yet. Entries you log in Operations or Networking that reference this contact will appear here.
            </div>
          )}
          {activity.map((a, i) => {
            const e = a.entry;
            const key = `${a.collection}-${e.id || i}`;
            const isOpen = openItem === key;
            return (
              <div key={key} className="bg-white border border-[var(--rw-border)] rounded-[4px]">
                <button
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[var(--rw-surface-2)]"
                  onClick={() => setOpenItem(isOpen ? null : key)}
                  data-testid={`activity-toggle-${i}`}
                >
                  <input
                    type="checkbox"
                    checked={!!e.follow_up_completed || (e.status || "").toLowerCase() === "completed"}
                    readOnly
                    className="rounded-sm"
                  />
                  <span className="nav-dot" style={{ background: SOURCE_DOT[a.source] || "#999" }} />
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">
                      {a.source}
                    </div>
                    <div className="font-semibold">
                      {e.individual || e.name || e.task || e.borrowers || e.company || "(untitled)"}
                    </div>
                  </div>
                  <div className="text-xs text-neutral-500 font-mono-rw">{e.date || e.month || ""}</div>
                  <StatusPill value={e.status} />
                </button>
                {isOpen && (
                  <div className="border-t border-[var(--rw-border)] p-4 grid grid-cols-2 gap-3 text-sm bg-[var(--rw-surface-2)]/40">
                    <Detail label="Category" value={e.category || e.section || e.type} />
                    <Detail label="Company" value={e.company || e.introduced_company} />
                    <Detail label="Priority" value={e.priority} />
                    <Detail label="Follow-up" value={e.follow_up} />
                    <Detail label="Follow-up date" value={e.follow_up_date} />
                    <Detail label="Date" value={e.date || e.created_at} />
                    <div className="col-span-2">
                      <Detail label="Notes" value={e.notes} block />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value, block }) {
  return (
    <div className={block ? "" : ""}>
      <div className="label-eyebrow">{label}</div>
      <div className="text-sm mt-0.5 whitespace-pre-wrap">{value || "—"}</div>
    </div>
  );
}
